import React, { useState } from 'react';
import { Filter, X, Zap } from 'lucide-react';

import { Button } from '@/component-library/primitives/Button/Button';
import { Input } from '@/component-library/widgets/Input/Input';

export interface QuickFilterCriteria {
  // Risk level filter
  riskLevel?: 'high' | 'medium' | 'low' | null;

  // Email domain filter
  emailDomain?: string | null;

  // Risk score range
  riskScoreFrom?: number | null;
  riskScoreTo?: number | null;

  // Status filter
  status?: 'all' | 'active' | 'cancelled' | 'past_due' | 'past_customer' | 'free_trial' | 'free' | 'new_user' | null;

  // Signal-based filter (for signals that can be resolved with discount)
  signalActionType?: 'discount' | 'upgrade' | 'engagement' | null;
}

export interface QuickFilterPreset {
  id: string;
  name: string;
  description: string;
  icon?: React.ReactNode;
  criteria: QuickFilterCriteria;
}

interface QuickFilterProps {
  onApply: (criteria: QuickFilterCriteria) => void;
  onClear: () => void;
  activeCriteria?: QuickFilterCriteria;
  presets?: QuickFilterPreset[];
}

const defaultPresets: QuickFilterPreset[] = [
  {
    id: 'high-risk-instagram',
    name: 'High Risk + Instagram',
    description: 'Users with HIGH RISK and @instagram.com email domain',
    criteria: {
      riskLevel: 'high',
      emailDomain: 'instagram.com',
    },
  },
  {
    id: 'risk-score-60-85',
    name: 'Risk Score 60-85%',
    description: 'Users with risk score between 60% and 85%',
    criteria: {
      riskScoreFrom: 60,
      riskScoreTo: 85,
    },
  },
  {
    id: 'discount-signals',
    name: 'Discount Action Signals',
    description: 'Signals that can be resolved with discount',
    criteria: {
      signalActionType: 'discount',
    },
  },
  {
    id: 'high-risk-active',
    name: 'High Risk Active Users',
    description: 'Active users with high churn risk',
    criteria: {
      riskLevel: 'high',
      status: 'active',
    },
  },
];

export const QuickFilter: React.FC<QuickFilterProps> = ({
  onApply,
  onClear,
  activeCriteria,
  presets = defaultPresets,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localCriteria, setLocalCriteria] = useState<QuickFilterCriteria>(activeCriteria || {});
  const [customPresetName, setCustomPresetName] = useState('');
  const [showCustomForm, setShowCustomForm] = useState(false);

  const hasActiveFilters = Boolean(
    activeCriteria?.riskLevel ||
      activeCriteria?.emailDomain ||
      activeCriteria?.riskScoreFrom !== null ||
      activeCriteria?.riskScoreTo !== null ||
      activeCriteria?.status ||
      activeCriteria?.signalActionType,
  );

  const handlePresetClick = (preset: QuickFilterPreset) => {
    setLocalCriteria(preset.criteria);
    onApply(preset.criteria);
    setIsExpanded(false);
  };

  const handleApplyCustom = () => {
    onApply(localCriteria);
    setIsExpanded(false);
  };

  const handleClear = () => {
    setLocalCriteria({});
    onClear();
    setIsExpanded(false);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (activeCriteria?.riskLevel) count++;
    if (activeCriteria?.emailDomain) count++;
    if (activeCriteria?.riskScoreFrom !== null || activeCriteria?.riskScoreTo !== null) count++;
    if (activeCriteria?.status) count++;
    if (activeCriteria?.signalActionType) count++;
    return count;
  };

  return (
    <div className="relative">
      <Button
        onClick={() => setIsExpanded(!isExpanded)}
        variant="transparent"
        className="relative flex items-center gap-2"
        data-filter-trigger
      >
        <Filter size={18} />
        <span>Quick Filter</span>
        {hasActiveFilters && (
          <span className="absolute -top-1 -right-1 bg-custom-red text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {getActiveFilterCount()}
          </span>
        )}
      </Button>

      {isExpanded && (
        <div
          className="absolute z-50 top-full right-0 mt-2 bg-white border border-custom-lightSilver rounded-lg shadow-lg p-6 w-[32rem] max-w-[calc(100vw-4rem)]"
          style={{ maxHeight: 'calc(100vh - 8rem)', overflowY: 'auto' }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Zap size={20} className="text-custom-blue" />
              <h3 className="text-lg font-semibold text-custom-black">Quick Filter</h3>
            </div>
            <button
              onClick={() => setIsExpanded(false)}
              className="text-custom-darkSilver hover:text-custom-black transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Preset Filters */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-custom-darkSilver mb-3">Preset Filters</label>
            <div className="grid grid-cols-1 gap-2">
              {presets.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => handlePresetClick(preset)}
                  className="text-left p-3 border border-custom-lightSilver rounded-lg hover:border-custom-blue hover:bg-blue-50 transition-colors"
                >
                  <div className="font-medium text-custom-black text-sm">{preset.name}</div>
                  <div className="text-xs text-custom-darkSilver mt-1">{preset.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Filter Form */}
          <div className="border-t border-custom-lightSilver pt-4">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-custom-darkSilver">Custom Filter</label>
              <button
                onClick={() => setShowCustomForm(!showCustomForm)}
                className="text-xs text-custom-blue hover:text-custom-darkBlue"
              >
                {showCustomForm ? 'Hide' : 'Show'} Custom
              </button>
            </div>

            {showCustomForm && (
              <div className="space-y-4">
                {/* Risk Level */}
                <div>
                  <label className="block text-sm font-medium text-custom-darkSilver mb-2">Risk Level</label>
                  <select
                    className="w-full text-sm border border-custom-lightSilver rounded px-3 py-2 bg-white text-custom-darkSilver focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={localCriteria.riskLevel || 'all'}
                    onChange={(e) =>
                      setLocalCriteria({
                        ...localCriteria,
                        riskLevel: e.target.value === 'all' ? null : (e.target.value as 'high' | 'medium' | 'low'),
                      })
                    }
                  >
                    <option value="all">All</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>

                {/* Email Domain */}
                <div>
                  <label className="block text-sm font-medium text-custom-darkSilver mb-2">Email Domain</label>
                  <Input
                    type="text"
                    placeholder="e.g., instagram.com"
                    value={localCriteria.emailDomain || ''}
                    onChange={(e) =>
                      setLocalCriteria({
                        ...localCriteria,
                        emailDomain: e.target.value.trim() || null,
                      })
                    }
                  />
                  <p className="text-xs text-custom-darkSilver mt-1">Enter domain without @ (e.g., instagram.com)</p>
                </div>

                {/* Risk Score Range */}
                <div>
                  <label className="block text-sm font-medium text-custom-darkSilver mb-2">Risk Score Range</label>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <Input
                        type="number"
                        placeholder="From"
                        value={localCriteria.riskScoreFrom?.toString() || ''}
                        onChange={(e) => {
                          const value = e.target.value ? parseInt(e.target.value, 10) : null;
                          if (value !== null && (value < 0 || value > 100)) return;
                          setLocalCriteria({
                            ...localCriteria,
                            riskScoreFrom: value,
                          });
                        }}
                      />
                    </div>
                    <span className="text-custom-darkSilver">-</span>
                    <div className="flex-1">
                      <Input
                        type="number"
                        placeholder="To"
                        value={localCriteria.riskScoreTo?.toString() || ''}
                        onChange={(e) => {
                          const value = e.target.value ? parseInt(e.target.value, 10) : null;
                          if (value !== null && (value < 0 || value > 100)) return;
                          setLocalCriteria({
                            ...localCriteria,
                            riskScoreTo: value,
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-custom-darkSilver mb-2">Status</label>
                  <select
                    className="w-full text-sm border border-custom-lightSilver rounded px-3 py-2 bg-white text-custom-darkSilver focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={localCriteria.status || 'all'}
                    onChange={(e) =>
                      setLocalCriteria({
                        ...localCriteria,
                        status: e.target.value === 'all' ? null : (e.target.value as any),
                      })
                    }
                  >
                    <option value="all">All</option>
                    <option value="active">Active</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="past_due">Past Due</option>
                    <option value="past_customer">Past Customer</option>
                    <option value="free_trial">Free Trial</option>
                    <option value="free">Free</option>
                    <option value="new_user">New User</option>
                  </select>
                </div>

                {/* Signal Action Type (for signals page) */}
                <div>
                  <label className="block text-sm font-medium text-custom-darkSilver mb-2">Action Type</label>
                  <select
                    className="w-full text-sm border border-custom-lightSilver rounded px-3 py-2 bg-white text-custom-darkSilver focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={localCriteria.signalActionType || 'all'}
                    onChange={(e) =>
                      setLocalCriteria({
                        ...localCriteria,
                        signalActionType: e.target.value === 'all' ? null : (e.target.value as any),
                      })
                    }
                  >
                    <option value="all">All</option>
                    <option value="discount">Discount</option>
                    <option value="upgrade">Upgrade</option>
                    <option value="engagement">Engagement</option>
                  </select>
                  <p className="text-xs text-custom-darkSilver mt-1">Filter signals by recommended action type</p>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-custom-lightSilver">
            <Button variant="transparent" onClick={handleClear} disabled={!hasActiveFilters}>
              Clear All
            </Button>
            {showCustomForm && <Button onClick={handleApplyCustom}>Apply Custom</Button>}
          </div>
        </div>
      )}
    </div>
  );
};
